import { set } from 'lodash-es';
import * as types from './mutations-types';
import { clientApi } from '@phoenix/clientApi';

const ALL_STRAPS_KEY = 'all';

const initialState = {
  watchReference: null,
  watchCompleteReference: '',
  watchExists: false,
  straps: {},
  filters: [],
  suggestions: [],
  allSortedItems: [],
  isLoading: false,
  isLoadingSuggestions: false,
  blockFiltersId: 0,
  filtersCta: null,
};

const getters = {
  allStraps(state) {
    return state.straps[ALL_STRAPS_KEY] ?? [];
  },

  allStrapsSortedItems(state) {
    return state.allSortedItems[ALL_STRAPS_KEY] ?? [];
  },

  allStrapsFilters(state) {
    return state.filters[ALL_STRAPS_KEY] ?? [];
  },

  strapsForCurrentRef(state) {
    return state.straps[state.watchReference] ?? [];
  },

  currentRefSortedItems(state) {
    return state.allSortedItems[state.watchReference] ?? [];
  },

  filtersForCurrentRef(state) {
    return state.filters[state.watchReference] ?? [];
  },

  hasResult(state, getters) {
    return getters.strapsForCurrentRef?.length > 0;
  },

  hasAllStrapsResult(state) {
    return state.straps[ALL_STRAPS_KEY]?.length > 0;
  },

  productsToDisplay(state, getters) {
    return {
      straps: getters.hasResult ? getters.strapsForCurrentRef : getters.allStraps,
      filters: getters.hasResult ? getters.filtersForCurrentRef : getters.allStrapsFilters,
      allSortedItems: getters.hasResult ? getters.currentRefSortedItems : getters.allStrapsSortedItems,
    };
  },
};

const actions = {
  async getAllStraps({ state, commit }) {
    commit(types.STRAP_SEARCH_REQUEST);

    try {
      const requestResult = await clientApi.get(window.route('api.search.watchRefResult'), {
        params: {
          blockFiltersId: state.blockFiltersId,
        },
      });

      const data = requestResult?.data?.data;
      if (!data) {
        throw 'No data found in request result';
      }

      commit(types.STRAP_SEARCH_SUCCESS, { data, isAllStraps: true });
    } catch (error) {
      commit(types.STRAP_SEARCH_FAILURE, { isAllStraps: true });
    }
  },

  async getFilteredStraps({ state, commit }, mandatoryFilters) {
    commit(types.STRAP_SEARCH_REQUEST);

    try {
      const requestResult = await clientApi.get(window.route('api.search.watchRefResult'), {
        params: {
          blockFiltersId: state.blockFiltersId,
          ...mandatoryFilters,
        },
      });

      const data = requestResult?.data?.data;
      if (!data) {
        throw 'No data found in request result';
      }

      commit(types.STRAP_SEARCH_SUCCESS, { data, isAllStraps: true });
    } catch (error) {
      commit(types.STRAP_SEARCH_FAILURE, { isAllStraps: true });
    }
  },

  async getStrapFromWatchReference({ state, commit }) {
    commit(types.STRAP_SEARCH_REQUEST);

    try {
      const requestResult = await clientApi.get(window.route('api.search.watchRefResult'), {
        params: {
          reference: state.watchReference,
          blockFiltersId: state.blockFiltersId,
        },
      });

      const data = requestResult?.data?.data;
      if (!data) {
        throw 'No data found in request result';
      }

      commit(types.STRAP_SEARCH_SUCCESS, { data });
    } catch (error) {
      commit(types.STRAP_SEARCH_FAILURE);
    }
  },

  async getSearchAsYouTypeWatchRef({ commit }, query) {
    commit(types.STRAP_SEARCH_SUGGESTION_REQUEST);

    let hasError = false;

    try {
      const requestResult = await clientApi.get(window.route('api.search.watchRef'), {
        params: {
          query,
        },
      });

      const data = requestResult?.data?.data;
      if (!data) {
        hasError = true;
      }

      commit(types.STRAP_SEARCH_SET_SUGGESTIONS, data);
    } catch (error) {
      hasError = true;
    }

    if (hasError) {
      commit(types.STRAP_SEARCH_SUGGESTIONS_FAILURE);
    }
  },
};

const mutations = {
  [types.STRAP_SEARCH_REQUEST](state) {
    state.isLoading = true;
  },

  [types.STRAP_SEARCH_SUGGESTION_REQUEST](state) {
    state.isLoadingSuggestions = true;
  },

  [types.STRAP_SEARCH_SUCCESS](state, payload) {
    const { data, isAllStraps = false } = payload;
    const currentRefKey = isAllStraps ? ALL_STRAPS_KEY : state.watchReference;

    state.straps[currentRefKey] = data.products ?? [];
    state.allSortedItems[currentRefKey] = data.allSortedItems ?? [];
    state.filters[currentRefKey] = data.filters ?? [];
    state.filtersCta = data.filtersCta ?? null;

    state.watchCompleteReference = data.watchCompleteReference;

    if (!isAllStraps) {
      state.watchExists = data.watchExists ?? false;
    }

    state.isLoading = false;
    state.suggestions = [];
  },

  [types.STRAP_SEARCH_FAILURE](state, payload) {
    const { isAllStraps = false } = payload ?? {};
    const currentRefKey = isAllStraps ? ALL_STRAPS_KEY : state.watchReference;

    state.straps[currentRefKey] = [];
    state.allSortedItems[currentRefKey] = [];
    state.filters[currentRefKey] = [];

    state.watchCompleteReference = '';

    if (!isAllStraps) {
      state.watchExists = false;
    }

    state.isLoading = false;
  },

  [types.STRAP_SEARCH_SUGGESTIONS_FAILURE](state) {
    state.suggestions = [];
    state.isLoadingSuggestions = false;
  },

  [types.STRAP_SEARCH_SET_WATCH_REF](state, watchReference) {
    state.watchReference = watchReference;
  },

  [types.STRAP_SEARCH_SET_BLOCK_FILTERS_ID](state, blockFiltersId) {
    state.blockFiltersId = blockFiltersId;
  },

  [types.STRAP_SEARCH_SET_SUGGESTIONS](state, suggestions) {
    state.suggestions = suggestions;
    state.isLoadingSuggestions = false;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
